import React from 'react';
import { getSpecData } from './productDetails/utils';
import OvenSection from './productDetails/OvenSection';
import OvenPowderCoatingPage from './productDetails/OvenPowderCoatingPage';
import OvenTechnicalSpecPage from './productDetails/OvenTechnicalSpecPage';
import HeatingOptionsSection from './productDetails/HeatingOptionsSection';
import ConveyorSection from './productDetails/ConveyorSection';
import RecoveryBoothPreTreatmentSection from './productDetails/RecoveryBoothPreTreatmentSection';
import GemaSectionPart1 from './productDetails/GemaSectionPart1';
import GemaSectionPart2 from './productDetails/GemaSectionPart2';
import GemaManualGunSection from './productDetails/GemaManualGunSection';
import PreTreatmentLine from './productDetails/PreTreatmentLine';
import CycloneSection from './productDetails/CycloneSection';
import DustCollectorSection from './productDetails/DustCollectorSection';
import PowderTransferSystemSection from './productDetails/PowderTransferSystemSection';
import HeadingDescription from './HeadingDescription';
import TechnicalDataTable from './TechnicalDataTable';
import ProposalImage from './ProposalImage';
import proposalImages, { imageConfigs } from '../../assets/images/proposal';
import { cyclone, dust_collector, powder_transfer_system } from '../../data/quotation/quotation_other_content';

function normalizeTableRows(rows = []) {
  return (rows || [])
    .map((row) => {
      const label = row?.label ?? row?.Parameter ?? row?.parameter ?? '';
      const value = row?.value ?? row?.Value ?? '';
      return { Parameter: label, Value: value };
    })
    .filter((row) => String(row.Parameter || '').trim() || String(row.Value || '').trim());
}

function extractTechnicalTables(technicalData) {
  if (!technicalData) return [];
  if (Array.isArray(technicalData.tables) && technicalData.tables.length > 0) {
    return technicalData.tables
      .map((table, idx) => ({
        id: table?.id || `table-${idx}`,
        label: table?.label || `Technical Specification ${idx + 1}`,
        headers: table?.headers || ['Parameter', 'Value'],
        data: normalizeTableRows(table?.data || []),
      }))
      .filter((table) => table.data.length > 0);
  }

  const data = normalizeTableRows(technicalData?.data || []);
  if (!data.length) return [];
  return [
    {
      id: 'single-table',
      label: technicalData?.label || 'Technical Specification',
      headers: technicalData?.headers || ['Parameter', 'Value'],
      data,
    },
  ];
}

export default function QuotationProductDetailsPages({
  sections = [],
  technicalSpecification = {},
  recoveryBoothContent,
  preTreatmentSteps,
  gemaAutomaticSystem,
  selectedProducts = [],
}) {
  const ts = technicalSpecification;
  const selectedProductKeys = new Set((selectedProducts || []).map((p) => p?.productKey).filter(Boolean));
  const hasSelectionFilter = selectedProductKeys.size > 0;
  const shouldShow = (productKey) => !hasSelectionFilter || selectedProductKeys.has(productKey);
  const selectedByProductKey = (productKey) => (selectedProducts || []).filter((p) => p?.productKey === productKey);
  const firstSelectedForKey = (productKey) => selectedByProductKey(productKey)[0] || null;

  const ovenSection = sections.find((s) => s.title === 'OVEN' && s.types);
  const heatingSection = sections.find((s) => s.title === 'Industrial Heating Options');
  const gemaSection = sections.find((s) => s.content_key === 'GEMA_Automatic_System');

  const selectedOven = firstSelectedForKey('oven');
  const selectedConveyor = firstSelectedForKey('conveyor');
  const selectedBooth = firstSelectedForKey('booth');
  const selectedCyclone = firstSelectedForKey('cyclone');
  const selectedDustCollector = firstSelectedForKey('dust_collector');
  const selectedPtLine = firstSelectedForKey('pt_line');
  const selectedPts = firstSelectedForKey('powder_transfer_system');

  const selectedOvenUploadedImage =
    selectedOven?.uploadedImages?.[0]?.url ||
    selectedOven?.uploadedImages?.[0]?.previewUrl ||
    null;

  const selectedConveyorUploadedImage =
    selectedConveyor?.uploadedImages?.[0]?.url ||
    selectedConveyor?.uploadedImages?.[0]?.previewUrl ||
    null;

  const selectedBoothUploadedImage =
    selectedBooth?.uploadedImages?.[0]?.url ||
    selectedBooth?.uploadedImages?.[0]?.previewUrl ||
    null;

  const selectedPtLineUploadedImage =
    selectedPtLine?.uploadedImages?.[0]?.url ||
    selectedPtLine?.uploadedImages?.[0]?.previewUrl ||
    null;

  const selectedPtsUploadedImage =
    selectedPts?.uploadedImages?.[0]?.url ||
    selectedPts?.uploadedImages?.[0]?.previewUrl ||
    null;

  const selectedCycloneOption = selectedCyclone?.selectionPath?.slice(-1)[0];
  const selectedCycloneImageKey = selectedCycloneOption?.value;
  const selectedCycloneUploadedImage =
    selectedCyclone?.uploadedImages?.[0]?.url ||
    selectedCyclone?.uploadedImages?.[0]?.previewUrl ||
    null;
  const selectedCycloneImage =
    selectedCycloneUploadedImage ||
    imageConfigs?.cyclone?.[selectedCycloneImageKey]?.[0] ||
    Object.values(proposalImages?.cyclone || {})[0] ||
    null;

  const selectedDustCollectorOption = selectedDustCollector?.selectionPath?.slice(-1)[0];
  const selectedDustCollectorImageKey = selectedDustCollectorOption?.value;
  const selectedDustCollectorUploadedImage =
    selectedDustCollector?.uploadedImages?.[0]?.url ||
    selectedDustCollector?.uploadedImages?.[0]?.previewUrl ||
    null;
  const selectedDustCollectorImage =
    selectedDustCollectorUploadedImage ||
    imageConfigs?.dust_collector?.[selectedDustCollectorImageKey]?.[0] ||
    Object.values(proposalImages?.dustCollector || {})[0] ||
    null;

  const conveyorTables = extractTechnicalTables(selectedConveyor?.technicalData);
  const cycloneTables = extractTechnicalTables(selectedCyclone?.technicalData);
  const dustCollectorTables = extractTechnicalTables(selectedDustCollector?.technicalData);
  const boothTables = extractTechnicalTables(selectedBooth?.technicalData);
  const ptTables = extractTechnicalTables(selectedPtLine?.technicalData);
  const ptsTables = extractTechnicalTables(selectedPts?.technicalData);

  const ovenTables = extractTechnicalTables(selectedOven?.technicalData);
  const ovenSpec = ovenTables[0]
    ? { data: ovenTables[0].data, headers: ovenTables[0].headers, label: ovenTables[0].label }
    : getSpecData(ts, 'powder_coating_curing_oven');
  const conveyorSpec = conveyorTables[0]
    ? { data: conveyorTables[0].data, headers: conveyorTables[0].headers }
    : getSpecData(ts, 'over_head_conveyor');
  const recoverySpec = boothTables[0]
    ? { data: boothTables[0].data, headers: boothTables[0].headers }
    : getSpecData(ts, 'recovery_booth_pollution_free');
  const ptSpec = ptTables[0]
    ? { data: ptTables[0].data, headers: ptTables[0].headers }
    : getSpecData(ts, 'pre_treatment_process');
  const ptStepsData = preTreatmentSteps && Object.values(preTreatmentSteps)[0];
  const selectedGema = firstSelectedForKey('gema_automatic_system');
  const activeGemaComponentKeys = selectedGema?.components?.map((c) => c.key) ?? null;
  const gema = activeGemaComponentKeys
    ? Object.fromEntries(
        Object.entries(gemaAutomaticSystem || {}).filter(([key]) => activeGemaComponentKeys.includes(key))
      )
    : (gemaAutomaticSystem || {});

  const dedicatedProductKeys = new Set(['oven', 'conveyor', 'booth', 'pt_line', 'powder_transfer_system', 'manual_gun', 'gema_automatic_system']);
  const additionalProducts = (selectedProducts || [])
    .filter((p) => p?.productKey && !dedicatedProductKeys.has(p.productKey))
    .map((p) => ({
      ...p,
      tables: extractTechnicalTables(p.technicalData),
    }))
    .filter((p) => p.tables.length > 0);

  return (
    <>
      {shouldShow('oven') && ovenSection && <OvenSection ovenSection={ovenSection} />}

      {shouldShow('oven') && ovenSection && (
        <>
          <OvenPowderCoatingPage ovenSection={ovenSection} uploadedImage={selectedOvenUploadedImage} />
          {ovenSpec && <OvenTechnicalSpecPage ovenSpec={ovenSpec} />}
        </>
      )}

      {shouldShow('oven') && heatingSection && <HeatingOptionsSection heatingSection={heatingSection} />}

      {shouldShow('conveyor') && conveyorSpec && <ConveyorSection conveyorSpec={conveyorSpec} uploadedImage={selectedConveyorUploadedImage} />}

      {shouldShow('booth') && (
        <RecoveryBoothPreTreatmentSection
          recoveryBoothContent={recoveryBoothContent}
          recoverySpec={recoverySpec}
          ptSpec={null}
          ptStepsData={null}
          uploadedImage={selectedBoothUploadedImage}
        />
      )}
      {shouldShow('dust_collector') && selectedDustCollector && (
        <DustCollectorSection
          dustCollectorContent={dust_collector}
          dustCollectorImage={selectedDustCollectorImage}
          dustCollectorTables={dustCollectorTables}
          isUploadedImage={!!selectedDustCollectorUploadedImage}
        />
      )}
      {shouldShow('cyclone') && selectedCyclone && (
        <CycloneSection
          cycloneContent={cyclone}
          cycloneImage={selectedCycloneImage}
          cycloneTables={cycloneTables}
          isUploadedImage={!!selectedCycloneUploadedImage}
        />
      )}

      {shouldShow('powder_transfer_system') && selectedPts && (
        <PowderTransferSystemSection
          ptsContent={powder_transfer_system}
          ptsImage={selectedPtsUploadedImage || null}
          ptsTables={ptsTables}
          isUploadedImage={!!selectedPtsUploadedImage}
        />
      )}

      {shouldShow('pt_line') && (
        <PreTreatmentLine
          recoveryBoothContent={null}
          recoverySpec={null}
          ptSpec={ptSpec}
          ptStepsData={ptStepsData}
          uploadedImage={selectedPtLineUploadedImage}
        />
      )}

      {shouldShow('gema_automatic_system') && gemaSection && <GemaSectionPart1 gemaSection={gemaSection} gema={gema} />}

      {shouldShow('gema_automatic_system') && gemaSection && <GemaSectionPart2 gema={gema} />}

      {shouldShow('manual_gun') && <GemaManualGunSection manualGunSpec={ts?.manual_gun} />}

      {additionalProducts.map((product) => {
        const uploadedImg =
          product.uploadedImages?.[0]?.url ||
          product.uploadedImages?.[0]?.previewUrl ||
          null;
        return (
          <div key={product.id || product.title} className="a4-paddings quotation-page quotation-page-compact">
            <div className="quotation-page-inner quotation-bg-light">
              <div className="quotation-section-block">
                <HeadingDescription heading={product.title || 'Product'} description="" level={2} />
                {uploadedImg && (
                  <ProposalImage
                    src={uploadedImg}
                    wrapperStyle={{ marginBottom: 12 }}
                    imgStyle={{ maxHeight: 500, width: 'auto', objectFit: 'contain' }}
                  />
                )}
                {product.tables.map((table) => (
                  <TechnicalDataTable
                    key={`${product.id || product.title}-${table.id}`}
                    data={table.data}
                    headers={table.headers}
                    title={table.label}
                  />
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}
